import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { getPattern } from '../../../lib/get-pattern';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';

@Component({
  selector: 'app-emails-entry-field',
  templateUrl: './emails-entry-field.component.html',
  styleUrls: ['./emails-entry-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, TooltipDirective, ButtonDirective, SvgComponent],
})
export class EmailsEntryFieldComponent {
  @Input() validationMethod: (email: string) => boolean;
  @Input() validationErrorMessageMethod: (email: string) => string;
  @Input() autoCompleteMethod: (email: string) => string;
  @Input() customPlaceholder: string;
  @Input() noPlaceholder: boolean;
  @Input() textareaStyle: boolean;
  @Input() verticallyCenter: boolean;
  @Input() noBorder: boolean;
  @Input() maxCount: number;
  @Output() onChange: EventEmitter<[boolean, string]> = new EventEmitter();
  @Output() onFocus = new EventEmitter();
  @Output() onBlur = new EventEmitter();
  emails = signal<string[]>([]);
  emailPattern = getPattern('email');
  form = new FormGroup({
    emails: new FormControl(''),
  });
  focused = signal(false);

  private formWatcher = this.form.valueChanges.subscribe(() => {
    const emails = this.form.value.emails || '';
    if (emails.includes(',') || emails.includes(' ') || emails.includes('\n')) {
      this.parseEmails();
    }
    this.emitOnChange();
  });

  isInvalid(email: string, index: number) {
    if (!email.match(this.emailPattern)) return true;
    const emailsLowerCase = this.emails().map(email => email.toLowerCase());
    const emailLowerCase = email.toLowerCase();
    if (emailsLowerCase.indexOf(emailLowerCase) !== emailsLowerCase.lastIndexOf(emailLowerCase)) {
      if (emailsLowerCase.indexOf(emailLowerCase) !== index) return true;
    }
    if (this.maxCount !== undefined && index + 1 > this.maxCount) return true;
    if (!this.validationMethod) return false;
    return !this.validationMethod(email);
  }

  getError(email: string, index: number): string {
    if (!email.match(this.emailPattern)) return 'Invalid email address';
    const emails = this.emails();
    if (emails.indexOf(email) !== emails.lastIndexOf(email)) {
      if (emails.indexOf(email) !== index) return 'Duplicate email address';
    }
    if (this.maxCount !== undefined && index + 1 > this.maxCount) return 'Max seats exeeded';
    if (!this.validationErrorMessageMethod) return;
    return this.validationErrorMessageMethod(email);
  }

  removeEmail(index: number) {
    const emails = this.emails();
    emails.splice(index, 1);
    this.emails.set(emails);
    this.emitOnChange();
  }

  onKeyPress(event: KeyboardEvent) {
    if (['Space', 'Enter', ',', ' ', 'Tab'].includes(event.code)) {
      event.preventDefault();
      this.parseEmails();
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (['Tab'].includes(event.code)) {
      event.preventDefault();
      this.parseEmails();
    }

    if (event.code === 'Backspace') {
      if (!this.form.value.emails.length) {
        const emails = this.emails();
        const email = emails.pop() || '';
        this.emails.set(emails);
        this.form.controls.emails.setValue(email);
        this.emitOnChange();
      }
    }
  }

  focus() {
    this.onFocus.emit();
  }

  blur() {
    this.focused.set(false);
    const emails = this.getEmailsFromInput().filter((email, index) => !this.isInvalid(email, index));
    if (emails.length) this.parseEmails();
    this.onBlur.emit();
  }

  private getEmailsFromInput(autoComplete = false) {
    return this.form.value.emails
      .split('\n')
      .join(',')
      .split(' ')
      .join(',')
      .replace(/,+/g, ',')
      .split(',')
      .filter(email => email)
      .map(email => {
        if (!autoComplete) return email;
        if (!this.autoCompleteMethod) return email;
        return this.autoCompleteMethod(email);
      });
  }

  parseEmails() {
    const emails = this.getEmailsFromInput(true);
    this.emails.set([...this.emails(), ...emails]);
    this.form.controls.emails.setValue('');
  }

  public getEmails() {
    return Array.from(
      new Set(
        this.emails().filter((email, index) => {
          if (this.isInvalid(email, index)) return false;
          return true;
        })
      )
    );
  }

  emitOnChange() {
    this.onChange.emit([!!(this.emails().length || this.form.value.emails.length), this.form.value.emails]);
  }
}
